import Phaser from 'phaser';
import { COLORS, GAME_W, GAME_H, intToHex, setTheme } from '../utils/constants';
import { authManager } from '../systems/AuthManager';
import { soundManager } from '../systems/SoundManager';
import { showInput } from '../utils/inputs';
import { walletManager } from '../systems/WalletManager';
import { apiFetch } from '../config';

export default class TitleScene extends Phaser.Scene {
  private titleText!: Phaser.GameObjects.Text;
  private subtitleText!: Phaser.GameObjects.Text;
  private particles!: Phaser.GameObjects.Particles.ParticleEmitter;
  private blinkTimer!: Phaser.Time.TimerEvent;

  private avatarBtn!: Phaser.GameObjects.Container;
  private avatarGlow!: Phaser.GameObjects.Graphics;
  private avatarCircle!: Phaser.GameObjects.Graphics;
  private avatarText!: Phaser.GameObjects.Text;
  private dropdown!: Phaser.GameObjects.Container;
  private dropdownOverlay!: Phaser.GameObjects.Rectangle;
  private dropdownVisible = false;
  private settingsModal!: Phaser.GameObjects.Container;
  private settingsVisible = false;
  private inputContainer: HTMLDivElement | null = null;
  private coinsText!: Phaser.GameObjects.Text;
  private settingsPanel: Phaser.GameObjects.Container | null = null;

  constructor() {
    super({ key: 'TitleScene' });
  }

  create() {
    const bg = this.add.image(GAME_W / 2, GAME_H / 2, 'gradient');
    bg.setDisplaySize(GAME_W, GAME_H);
    bg.setTint(COLORS.bgDark);

    this.particles = this.add.particles(GAME_W / 2, GAME_H / 2, 'glow', {
      lifespan: 3000,
      speed: { min: 20, max: 80 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.6, end: 0 },
      alpha: { start: 0.6, end: 0 },
      blendMode: Phaser.BlendModes.ADD,
      quantity: 2,
    });

    const scanline = this.add.graphics();
    scanline.lineStyle(1, COLORS.cyan, 0.1);
    scanline.lineBetween(0, GAME_H / 2, GAME_W, GAME_H / 2);
    this.tweens.add({
      targets: scanline,
      y: { from: -GAME_H, to: GAME_H * 2 },
      duration: 4000,
      repeat: -1,
      ease: 'Linear',
    });

    this.titleText = this.add.text(GAME_W / 2, GAME_H / 2 - 140, '神经决斗', {
      fontFamily: 'Microsoft YaHei, Arial Black, Arial, sans-serif',
      fontSize: '72px',
      color: intToHex(COLORS.cyan),
      stroke: intToHex(COLORS.blue),
      strokeThickness: 6,
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.titleText.setShadow(3, 3, intToHex(COLORS.purple), 3, true, true);

    this.subtitleText = this.add.text(GAME_W / 2, GAME_H / 2 - 70, '身体，就是你的第二个控制器。', {
      fontFamily: 'Microsoft YaHei, sans-serif',
      fontSize: '22px',
      color: intToHex(COLORS.white),
    }).setOrigin(0.5).setAlpha(0);
    this.tweens.add({ targets: this.subtitleText, alpha: 0.7, duration: 1000, delay: 500 });

    // Main menu buttons
    this.createButton('开始战斗', COLORS.cyan, GAME_H / 2 - 10, () => {
      soundManager.play('ui_click');
      this.cameras.main.fadeOut(500, 0, 0, 30);
      this.time.delayedCall(500, () => {
        if (authManager.isLoggedIn()) this.scene.start('CameraSetupScene');
        else this.scene.start('LoginScene');
      });
    });

    this.createButton('PVP 对战', COLORS.red, GAME_H / 2 + 55, () => {
      soundManager.play('ui_click');
      this.cameras.main.fadeOut(500, 0, 0, 30);
      this.time.delayedCall(500, () => {
        this.scene.start('PVPScene', { p1Name: '玩家1', p2Name: '玩家2', local: true });
      });
    });

    this.createButton('商城', COLORS.yellow, GAME_H / 2 + 120, () => {
      soundManager.play('ui_click');
      this.cameras.main.fadeOut(500, 0, 0, 30);
      this.time.delayedCall(500, () => this.scene.start('ShopScene'));
    });

    this.createButton('排行榜', COLORS.purple, GAME_H / 2 + 185, () => {
      soundManager.play('ui_click');
      this.cameras.main.fadeOut(500, 0, 0, 30);
      this.time.delayedCall(500, () => this.scene.start('LeaderboardScene'));
    });

    this.blinkTimer = this.time.addEvent({
      delay: 800, loop: true,
      callback: () => { this.subtitleText.setVisible(!this.subtitleText.visible); },
    });

    this.input.keyboard!.on('keydown-ENTER', () => this.startBattle());
    this.input.keyboard!.on('keydown-L', () => this.showLeaderboard());

    this.createAvatar();
    this.createDropdown();
    this.settingsModal = this.add.container(0, 0).setDepth(50).setVisible(false);

    // Wallet display
    this.coinsText = this.add.text(GAME_W / 2, GAME_H - 80, `💰 ${walletManager.getCoins()}  💎 ${walletManager.getGems()}`, {
      fontFamily: 'Arial, sans-serif', fontSize: '14px', color: '#ffea00',
    }).setOrigin(0.5).setDepth(10);

    this.add.text(GAME_W / 2, GAME_H - 55, 'ENTER 开始 | L 排行榜', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '12px', color: '#333355',
    }).setOrigin(0.5).setDepth(10);

    this.add.text(GAME_W - 20, GAME_H - 20, 'Alpha v0.2.0', {
      fontFamily: 'Arial, sans-serif', fontSize: '12px', color: '#555577',
    }).setOrigin(1, 1).setDepth(10);

    this.input.keyboard!.on('keydown-ESC', () => {
      if (this.settingsVisible) this.closeSettings();
      else if (this.dropdownVisible) this.closeDropdown();
    });
  }

  private createAvatar() {
    const x = GAME_W - 50, y = 40, r = 24;
    const char = authManager.isLoggedIn() ? authManager.getPlayerName().charAt(0).toUpperCase() : '?';

    this.avatarGlow = this.add.graphics().setDepth(10);
    this.avatarGlow.fillStyle(COLORS.cyan, 0.12);
    this.avatarGlow.fillCircle(x, y, r + 10);
    this.tweens.add({
      targets: this.avatarGlow, alpha: { from: 0.5, to: 1 },
      duration: 1600, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });

    this.avatarCircle = this.add.graphics().setDepth(11);
    this.avatarCircle.fillStyle(authManager.isLoggedIn() ? COLORS.blue : COLORS.darkGray, 1);
    this.avatarCircle.fillCircle(x, y, r);
    this.avatarCircle.lineStyle(2, COLORS.cyan, 0.9);
    this.avatarCircle.strokeCircle(x, y, r);

    this.avatarText = this.add.text(x, y, char, {
      fontFamily: 'Arial, sans-serif', fontSize: '20px', color: intToHex(COLORS.cyan), fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(12);

    const hitArea = this.add.rectangle(x, y, r * 2, r * 2)
      .setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(13).setAlpha(0.001);
    hitArea.on('pointerdown', () => { soundManager.play('ui_click'); this.toggleDropdown(); });

    this.avatarBtn = this.add.container(0, 0, [this.avatarGlow, this.avatarCircle, this.avatarText, hitArea]);
    this.avatarBtn.setDepth(10);
  }

  private createDropdown() {
    const cx = GAME_W - 50, w = 150, h = 120;
    const bg = this.add.rectangle(cx, 72 + h / 2, w, h, 0x0a0a1a, 0.96)
      .setOrigin(0.5).setStrokeStyle(1, COLORS.cyan, 0.8);

    const settingsItem = this.add.text(cx, 72 + 16, '⚙  设置', {
      fontFamily: 'Microsoft YaHei, Arial, sans-serif', fontSize: '14px', color: intToHex(COLORS.cyan),
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    settingsItem.on('pointerover', () => settingsItem.setColor(intToHex(COLORS.white)));
    settingsItem.on('pointerout', () => settingsItem.setColor(intToHex(COLORS.cyan)));
    settingsItem.on('pointerdown', () => {
      soundManager.play('ui_click');
      this.closeDropdown();
      this.openSettings();
    });

    const shopItem = this.add.text(cx, 72 + 46, '🛒  商城', {
      fontFamily: 'Microsoft YaHei, Arial, sans-serif', fontSize: '14px', color: intToHex(COLORS.yellow),
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    shopItem.on('pointerover', () => shopItem.setColor(intToHex(COLORS.white)));
    shopItem.on('pointerout', () => shopItem.setColor(intToHex(COLORS.yellow)));
    shopItem.on('pointerdown', () => {
      soundManager.play('ui_click');
      this.closeDropdown();
      this.cameras.main.fadeOut(500, 0, 0, 30);
      this.time.delayedCall(500, () => this.scene.start('ShopScene'));
    });

    const logoutItem = this.add.text(cx, 72 + 76, authManager.isLoggedIn() ? '🚪  退出登录' : '🔑  登录', {
      fontFamily: 'Microsoft YaHei, Arial, sans-serif', fontSize: '14px', color: intToHex(COLORS.purple),
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    logoutItem.on('pointerover', () => logoutItem.setColor(intToHex(COLORS.red)));
    logoutItem.on('pointerout', () => logoutItem.setColor(intToHex(COLORS.purple)));
    logoutItem.on('pointerdown', () => {
      soundManager.play('ui_click');
      this.closeDropdown();
      if (authManager.isLoggedIn()) { authManager.logout(); this.scene.restart(); }
      else {
        this.cameras.main.fadeOut(500, 0, 0, 30);
        this.time.delayedCall(500, () => this.scene.start('LoginScene'));
      }
    });

    this.dropdown = this.add.container(0, 0, [bg, settingsItem, shopItem, logoutItem]);
    this.dropdown.setDepth(20).setVisible(false);
  }

  private toggleDropdown() {
    if (this.dropdownVisible) this.closeDropdown();
    else this.openDropdown();
  }

  private openDropdown() {
    this.dropdownVisible = true;
    this.dropdown.setVisible(true);
    this.dropdown.setAlpha(0);
    this.dropdownOverlay = this.add.rectangle(GAME_W / 2, GAME_H / 2, GAME_W, GAME_H, 0x000000, 0)
      .setInteractive().setDepth(15);
    this.dropdownOverlay.on('pointerdown', () => this.closeDropdown());
    this.tweens.add({ targets: this.dropdown, alpha: 1, duration: 120, ease: 'Power2' });
  }

  private closeDropdown() {
    this.dropdownVisible = false;
    this.dropdown.setVisible(false);
    if (this.dropdownOverlay) { this.dropdownOverlay.destroy(); this.dropdownOverlay = undefined as any; }
  }

  private openSettings() {
    if (this.settingsVisible) return;
    this.settingsVisible = true;
    this.settingsModal.removeAll(true);

    const pw = 460, ph = 420, px = GAME_W / 2, py = GAME_H / 2;
    const all: Phaser.GameObjects.GameObject[] = [];

    const overlay = this.add.rectangle(px, py, GAME_W, GAME_H, 0x000000, 0.7).setInteractive();
    overlay.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
      const bx = px - pw / 2, by = py - ph / 2;
      if (ptr.x < bx || ptr.x > bx + pw || ptr.y < by || ptr.y > by + ph) this.closeSettings();
    });
    all.push(overlay);

    const panel = this.add.rectangle(px, py, pw, ph, 0x0a0a1a, 0.97).setStrokeStyle(2, COLORS.cyan, 0.85);
    all.push(panel);

    all.push(this.add.text(px, py - ph / 2 + 28, '设置', {
      fontFamily: 'Microsoft YaHei, Arial, sans-serif', fontSize: '20px',
      color: intToHex(COLORS.cyan), fontStyle: 'bold',
    }).setOrigin(0.5));

    const closeX = this.add.text(px + pw / 2 - 18, py - ph / 2 + 10, '✕', {
      fontFamily: 'Arial, sans-serif', fontSize: '18px', color: '#555577',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    closeX.on('pointerdown', () => this.closeSettings());
    closeX.on('pointerover', () => closeX.setColor(intToHex(COLORS.cyan)));
    closeX.on('pointerout', () => closeX.setColor('#555577'));
    all.push(closeX);

    // Theme
    all.push(this.add.text(px - pw / 2 + 25, py - 100, '主题切换', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '13px', color: '#555577',
    }));
    const themes = ['亮色', '暗色', '赛博朋克'];
    const themeMap = ['light', 'dark', 'cyberpunk'];
    const curTheme = localStorage.getItem('neuro_duel_theme') || 'cyberpunk';
    const curIdx = themeMap.indexOf(curTheme);
    themes.forEach((name, i) => {
      const sel = i === curIdx;
      const t = this.add.text(px - 90 + i * 100, py - 73, name, {
        fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '13px',
        color: sel ? intToHex(COLORS.cyan) : '#555577',
        fontStyle: sel ? 'bold' : 'normal',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      t.on('pointerdown', () => { this.closeSettings(); setTheme(themeMap[i]); this.scene.restart(); });
      t.on('pointerover', () => { if (!sel) t.setColor(intToHex(COLORS.white)); });
      t.on('pointerout', () => { if (!sel) t.setColor('#555577'); });
      all.push(t);
    });

    // Divider
    const div1 = this.add.graphics();
    div1.lineStyle(1, COLORS.purple, 0.4);
    div1.lineBetween(px - pw / 2 + 20, py - 50, px + pw / 2 - 20, py - 50);
    all.push(div1);

    // Volume
    all.push(this.add.text(px - pw / 2 + 25, py - 36, '音量控制', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '13px', color: '#555577',
    }));
    const volSteps = ['静音', '低', '中', '高'];
    const volLabels = [0, 0.1, 0.3, 0.6];
    const curVol = parseFloat(localStorage.getItem('neuro_duel_volume') || '0.3');
    const curVolIdx = volLabels.findIndex(v => Math.abs(v - curVol) < 0.05);
    volSteps.forEach((label, i) => {
      const sel = i === (curVolIdx >= 0 ? curVolIdx : 2);
      const t = this.add.text(px - 90 + i * 60, py - 14, label, {
        fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '13px',
        color: sel ? intToHex(COLORS.cyan) : '#555577',
        fontStyle: sel ? 'bold' : 'normal',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      t.on('pointerdown', () => {
        localStorage.setItem('neuro_duel_volume', String(volLabels[i]));
        soundManager.play('ui_click');
        this.closeSettings();
        this.openSettings();
      });
      t.on('pointerover', () => { if (!sel) t.setColor(intToHex(COLORS.white)); });
      t.on('pointerout', () => { if (!sel) t.setColor('#555577'); });
      all.push(t);
    });

    // Divider 2
    const div2 = this.add.graphics();
    div2.lineStyle(1, COLORS.purple, 0.4);
    div2.lineBetween(px - pw / 2 + 20, py + 20, px + pw / 2 - 20, py + 20);
    all.push(div2);

    // Player name
    all.push(this.add.text(px - pw / 2 + 25, py + 34, '玩家名称', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '13px', color: '#555577',
    }));
    all.push(this.add.text(px - pw / 2 + 25, py + 56,
      authManager.isLoggedIn() ? authManager.getPlayerName() : '未登录', {
      fontFamily: 'Arial, sans-serif', fontSize: '15px', color: intToHex(COLORS.cyan),
    }));

    const editBtn = this.add.text(px + pw / 2 - 25, py + 56, '编辑', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '13px', color: intToHex(COLORS.purple),
    }).setOrigin(1, 0.5).setInteractive({ useHandCursor: true });
    editBtn.on('pointerdown', () => { if (authManager.isLoggedIn()) this.showNameInput(); });
    editBtn.on('pointerover', () => editBtn.setColor(intToHex(COLORS.white)));
    editBtn.on('pointerout', () => editBtn.setColor(intToHex(COLORS.purple)));
    all.push(editBtn);

    // Divider 3
    const div3 = this.add.graphics();
    div3.lineStyle(1, COLORS.purple, 0.4);
    div3.lineBetween(px - pw / 2 + 20, py + 90, px + pw / 2 - 20, py + 90);
    all.push(div3);

    // Input display
    all.push(this.add.text(px - pw / 2 + 25, py + 104, '输入方式', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '13px', color: '#555577',
    }));
    const savedInputs = localStorage.getItem('neuro_duel_input_mode') || 'keyboard';
    const inputMode = savedInputs === 'camera' ? '摄像头' : '键盘';
    const inputDisplay = this.add.text(px - pw / 2 + 25, py + 126, inputMode, {
      fontFamily: 'Arial, sans-serif', fontSize: '15px', color: intToHex(COLORS.cyan),
    });
    all.push(inputDisplay);

    const inputToggle = this.add.text(px + pw / 2 - 25, py + 126, '切换', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '13px', color: intToHex(COLORS.purple),
    }).setOrigin(1, 0.5).setInteractive({ useHandCursor: true });
    inputToggle.on('pointerdown', () => {
      const next = savedInputs === 'camera' ? 'keyboard' : 'camera';
      localStorage.setItem('neuro_duel_input_mode', next);
      soundManager.play('ui_click');
      // 不重启场景，直接更新显示
      inputDisplay.setText(next === 'camera' ? '摄像头' : '键盘');
      inputToggle.setColor(intToHex(COLORS.green));
      this.time.delayedCall(300, () => inputToggle.setColor(intToHex(COLORS.purple)));
    });
    inputToggle.on('pointerover', () => inputToggle.setColor(intToHex(COLORS.white)));
    inputToggle.on('pointerout', () => inputToggle.setColor(intToHex(COLORS.purple)));
    all.push(inputToggle);

    // Divider 4
    const div4 = this.add.graphics();
    div4.lineStyle(1, COLORS.purple, 0.4);
    div4.lineBetween(px - pw / 2 + 20, py + 150, px + pw / 2 - 20, py + 150);
    all.push(div4);

    // 修改密码
    all.push(this.add.text(px - pw / 2 + 25, py + 164, '修改密码', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '13px', color: '#555577',
    }));
    const pwEditBtn = this.add.text(px + pw / 2 - 25, py + 164, '修改', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '13px', color: intToHex(COLORS.purple),
    }).setOrigin(1, 0.5).setInteractive({ useHandCursor: true });
    pwEditBtn.on('pointerdown', () => {
      if (!authManager.isLoggedIn()) return;
      this.closeSettings();
      this.showPasswordChange();
    });
    pwEditBtn.on('pointerover', () => pwEditBtn.setColor(intToHex(COLORS.white)));
    pwEditBtn.on('pointerout', () => pwEditBtn.setColor(intToHex(COLORS.purple)));
    all.push(pwEditBtn);

    // Logout button
    all.push(this.settingsBtn(px, py + ph / 2 - 30, '退出登录', () => {
      this.closeSettings();
      authManager.logout();
      this.scene.restart();
    }));

    this.settingsModal.add(all);
    this.settingsModal.setVisible(true);
    this.settingsModal.setAlpha(0);
    this.tweens.add({ targets: this.settingsModal, alpha: 1, duration: 180, ease: 'Power2' });
  }

  private closeSettings() {
    this.settingsVisible = false;
    this.cleanupInputs();
    if (this.settingsModal) this.settingsModal.setVisible(false);
  }

  private showNameInput() {
    showInput('nd-input-name', GAME_W / 2, GAME_H / 2, (val) => {
      if (val && val.trim()) {
        try {
          const raw = localStorage.getItem('neuro_duel_auth');
          if (raw) {
            const data = JSON.parse(raw);
            data.playerName = val.trim();
            localStorage.setItem('neuro_duel_auth', JSON.stringify(data));
            (authManager as any).data = data;
          }
        } catch {}
      }
      this.scene.restart();
    });
  }

  private cleanupInputs() {
    if (this.inputContainer) {
      if (this.inputContainer.parentNode) this.inputContainer.remove();
      this.inputContainer = null;
    }
  }

  private createButton(text: string, color: number, yPos: number, callback: () => void): Phaser.GameObjects.Container {
    const w = 320, h = 46;
    const group = this.add.container(GAME_W / 2, yPos, [
      this.add.rectangle(0, 0, w, h, color).setOrigin(0.5).setAlpha(0.2),
      this.add.rectangle(0, 0, w, h, 0x000000).setOrigin(0.5).setStrokeStyle(2, color),
      this.add.text(0, 0, text, {
        fontFamily: 'Arial, sans-serif', fontSize: '20px',
        color: intToHex(color), fontStyle: 'bold',
      }).setOrigin(0.5),
    ]);
    group.setSize(w, h);
    group.setInteractive({ useHandCursor: true });
    group.on('pointerover', () => { (group.list[0] as Phaser.GameObjects.Rectangle).setAlpha(0.4); soundManager.play('ui_hover'); });
    group.on('pointerout', () => { (group.list[0] as Phaser.GameObjects.Rectangle).setAlpha(0.2); });
    group.on('pointerdown', callback);
    return group;
  }

  private settingsBtn(x: number, y: number, text: string, cb: () => void): Phaser.GameObjects.Container {
    const w = 200, h = 36;
    const btn = this.add.container(x, y, [
      this.add.rectangle(0, 0, w, h, COLORS.purple).setOrigin(0.5).setAlpha(0.2).setDepth(51),
      this.add.rectangle(0, 0, w, h, 0x000000).setOrigin(0.5).setStrokeStyle(1, COLORS.purple).setDepth(51),
      this.add.text(0, 0, text, {
        fontFamily: 'Microsoft YaHei, Arial, sans-serif', fontSize: '14px',
        color: intToHex(COLORS.purple), fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(51),
    ]);
    btn.setSize(w, h).setInteractive({ useHandCursor: true });
    btn.on('pointerover', () => (btn.list[0] as Phaser.GameObjects.Rectangle).setAlpha(0.4));
    btn.on('pointerout', () => (btn.list[0] as Phaser.GameObjects.Rectangle).setAlpha(0.2));
    btn.on('pointerdown', () => { soundManager.play('ui_click'); cb(); });
    return btn;
  }

  private startBattle() {
    this.blinkTimer?.remove();
    this.cameras.main.fadeOut(500, 0, 0, 30);
    this.time.delayedCall(500, () => this.scene.start('CameraSetupScene'));
  }

  private showLeaderboard() {
    this.blinkTimer?.remove();
    this.cameras.main.fadeOut(500, 0, 0, 30);
    this.time.delayedCall(500, () => this.scene.start('LeaderboardScene'));
  }

  private showPasswordChange() {
    const pw = 360, ph = 300;
    const px = GAME_W / 2, py = GAME_H / 2;
    const panel = this.add.container(0, 0).setDepth(100);
    this.settingsPanel = panel;

    panel.add(this.add.rectangle(0, 0, GAME_W, GAME_H, 0x000000).setOrigin(0).setAlpha(0.6).setInteractive());
    panel.add(this.add.rectangle(px, py, pw, ph, COLORS.bgPanel, 0.95).setStrokeStyle(2, COLORS.cyan));
    panel.add(this.add.text(px, py - ph / 2 + 30, '修改密码', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '22px', color: intToHex(COLORS.cyan), fontStyle: 'bold',
    }).setOrigin(0.5));

    let oldPass = '', newPass = '', confirmPass = '';
    const fields = [
      { label: '当前密码', y: py - 50, key: 'old' },
      { label: '新密码', y: py, key: 'new' },
      { label: '确认新密码', y: py + 50, key: 'confirm' },
    ];

    const displays: Phaser.GameObjects.Text[] = [];
    for (const f of fields) {
      panel.add(this.add.text(px - pw / 2 + 25, f.y - 12, f.label, {
        fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '12px', color: '#555577',
      }));
      panel.add(this.add.rectangle(px, f.y + 8, pw - 50, 32, COLORS.bgDark, 0.8).setStrokeStyle(1, COLORS.cyan, 0.3));
      const disp = this.add.text(px, f.y + 8, '点击输入', {
        fontFamily: 'Arial, sans-serif', fontSize: '14px', color: COLORS.textDim,
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      const key = f.key;
      disp.on('pointerdown', () => {
        showInput(`nd-pw-${key}`, px, f.y + 8, (val) => {
          if (key === 'old') oldPass = val;
          else if (key === 'new') newPass = val;
          else confirmPass = val;
          disp.setText(val ? '*'.repeat(val.length) : '点击输入');
          disp.setColor(val ? COLORS.text : COLORS.textDim);
        });
      });
      panel.add(disp);
      displays.push(disp);
    }

    const statusText = this.add.text(px, py + 110, '', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '13px', color: '#ff1744',
    }).setOrigin(0.5);
    panel.add(statusText);

    const submitBtn = this.settingsBtn(px - 55, py + 145, '确认修改', async () => {
      soundManager.play('ui_click');
      if (!oldPass || !newPass) { statusText.setText('请填写所有字段').setColor('#ff1744'); return; }
      if (newPass !== confirmPass) { statusText.setText('两次新密码不一致').setColor('#ff1744'); return; }
      statusText.setText('修改中...').setColor('#ffea00');
      const result = await authManager.changePassword(oldPass, newPass);
      if (result.success) {
        statusText.setText('修改成功！').setColor('#00e676');
        soundManager.play('challenge_success');
        this.time.delayedCall(1000, () => this.closeSettings());
      } else {
        statusText.setText(result.error || '修改失败').setColor('#ff1744');
        soundManager.play('challenge_fail');
      }
    });
    panel.add(submitBtn);

    const cancelBtn = this.settingsBtn(px + 55, py + 145, '取消', () => {
      soundManager.play('ui_click');
      this.closeSettings();
    });
    panel.add(cancelBtn);

    panel.setAlpha(0);
    this.tweens.add({ targets: panel, alpha: 1, duration: 200 });
  }
}
